/**
 * Zona horaria del local.
 *
 * Todo lo que el servidor guarda es un instante en UTC. Esta zona decide una
 * sola cosa, pero decisiva: qué día del calendario es ese instante. Una venta
 * de las 22:00 en Chile ocurre al día siguiente en UTC.
 *
 * La zona es la del **negocio**, no la del equipo. El dueño que revisa el
 * reporte desde el celular en otro país tiene que ver el día de la tienda; y
 * un equipo con la zona del sistema mal puesta no puede correr las fechas de
 * todos los demás.
 */

const POR_DEFECTO = 'America/Santiago'

let zona = POR_DEFECTO

/** La fija el arranque de la sesión, con lo que responde /configuracion. */
export function fijarZonaNegocio(identificador: string | null | undefined): void {
    if (!identificador) return

    try {
        // Se comprueba que el navegador la conozca antes de adoptarla: con una
        // zona inválida, Intl lanza en cada formateo y la pantalla queda en
        // blanco. Es preferible seguir con la de por defecto.
        new Intl.DateTimeFormat('en-CA', { timeZone: identificador })
        zona = identificador
    } catch {
        zona = POR_DEFECTO
    }
}

export function zonaNegocio(): string {
    return zona
}

const formateadores = new Map<string, Intl.DateTimeFormat>()

function formateadorDia(z: string): Intl.DateTimeFormat {
    let f = formateadores.get(z)

    if (!f) {
        // 'en-CA' da directamente YYYY-MM-DD, que es el formato con el que se
        // puede comparar y ordenar como texto.
        f = new Intl.DateTimeFormat('en-CA', {
            timeZone: z,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        })
        formateadores.set(z, f)
    }

    return f
}

/** Día del calendario (YYYY-MM-DD) en que cae un instante, visto desde la zona. */
export function diaEnZona(instante: number | Date, z: string = zona): string {
    return formateadorDia(z).format(instante)
}

/** Hoy en la zona del local, en YYYY-MM-DD. */
export function hoy(): string {
    return diaEnZona(Date.now())
}

export function primerDiaDelMes(): string {
    return `${hoy().slice(0, 7)}-01`
}

export function diaSiguiente(iso: string): string {
    const [a, m, d] = iso.split('-').map(Number)

    return new Date(Date.UTC(a!, m! - 1, d! + 1)).toISOString().slice(0, 10)
}

/**
 * Primer instante cuya hora de pared en la zona alcanza o supera `objetivo`.
 *
 * Es una búsqueda binaria y no una resta de horas porque **hay horas de pared
 * que no existen**. En Chile el horario de verano entra a las 24:00, así que el
 * 6 de septiembre de 2026 el reloj salta de las 23:59 del día 5 a la 01:00 del
 * día 6: ni «00:00» ni «00:30» de ese día ocurrieron nunca. Calcular restando
 * el desfase da un instante que en realidad pertenece al día anterior.
 *
 * Ante una hora que no existe devuelve el primer instante que sí existe después
 * de ella: el del cambio.
 *
 * `formatear` tiene que producir texto de ancho fijo y ordenable, como
 * `2026-09-06` o `2026-09-06T01:00`; así la comparación de textos equivale a
 * comparar instantes.
 */
function primerInstanteDesde(
    objetivo: string,
    centro: number,
    formatear: (ms: number) => string,
): Date {
    // 36 horas a cada lado cubren cualquier desfase del planeta con margen.
    let bajo = centro - 36 * 3600 * 1000
    let alto = centro + 36 * 3600 * 1000

    while (alto - bajo > 1) {
        const medio = bajo + Math.floor((alto - bajo) / 2)

        if (formatear(medio) < objetivo) bajo = medio
        else alto = medio
    }

    // La búsqueda supone que la hora de pared solo avanza, y hay un día al año
    // en que no: cuando el reloj se atrasa, esa hora se vive dos veces. En Chile
    // el 4 de abril de 2026 las 23:00–23:59 ocurren a las 02:00Z y otra vez a
    // las 03:00Z. Sobre una función que no es monótona, la binaria devuelve
    // cualquiera de las dos.
    //
    // Se retrocede por los saltos de horario que existen en el mundo —30, 45, 60
    // y 120 minutos— y se toma la primera aparición. Elegir siempre la primera
    // es una regla arbitraria, pero tiene que haber una: si no, la misma hora
    // guardada dos veces daría instantes distintos.
    if (formatear(alto) === objetivo) {
        for (const minutos of [120, 60, 45, 30]) {
            const antes = alto - minutos * 60000

            if (formatear(antes) === objetivo) {
                return new Date(antes)
            }
        }
    }

    return new Date(alto)
}

function medioDia(iso: string): number {
    const [a, m, d] = iso.split('-').map(Number)

    return Date.UTC(a!, m! - 1, d!)
}

/** Primer instante del día indicado, en la zona del local. */
export function inicioDelDia(iso: string, z: string = zona): Date {
    return primerInstanteDesde(iso, medioDia(iso), (ms) => diaEnZona(ms, z))
}

/**
 * Último instante del día: el anterior al primero del día siguiente.
 *
 * Así sale entero el día de 25 horas del cambio de otoño, que no termina a las
 * 23:59:59.999.
 */
export function finDelDia(iso: string, z: string = zona): Date {
    return new Date(inicioDelDia(diaSiguiente(iso), z).getTime() - 1)
}

/** El día completo como el par de instantes que espera la API. */
export function rangoDeDias(desde: string, hasta: string): { desde: string; hasta: string } {
    return {
        desde: inicioDelDia(desde).toISOString(),
        hasta: finDelDia(hasta).toISOString(),
    }
}

const formateadoresMinuto = new Map<string, Intl.DateTimeFormat>()

/** Hora de pared en la zona, con el formato exacto de un `datetime-local`. */
function enCampoLocal(instante: number | Date, z: string): string {
    let f = formateadoresMinuto.get(z)

    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', {
            timeZone: z,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        })
        formateadoresMinuto.set(z, f)
    }

    const p = Object.fromEntries(f.formatToParts(instante).map((x) => [x.type, x.value]))

    // Algunos navegadores devuelven "24" para la medianoche.
    const hora = String(Number(p.hour) % 24).padStart(2, '0')

    return `${p.year}-${p.month}-${p.day}T${hora}:${p.minute}`
}

/**
 * Convierte un instante al texto que espera un `<input type="datetime-local">`,
 * expresado en la zona del local. El campo no entiende zonas: recibe y devuelve
 * una hora de pared suelta, así que hay que ponerla y sacarla a mano.
 */
export function paraCampoLocal(iso: string | null): string {
    return iso ? enCampoLocal(new Date(iso), zona) : ''
}

/**
 * El inverso: la hora de pared que escribió el usuario, interpretada en la zona
 * del local, convertida al instante que se manda a la API.
 *
 * Usa la misma búsqueda que el inicio del día, y por el mismo motivo: sumarle
 * las horas al comienzo del día da mal justo el día en que el día no empieza a
 * medianoche. El 6 de septiembre de 2026 empieza a la 01:00, así que sumar una
 * hora dejaba las 02:00.
 */
export function desdeCampoLocal(valor: string): string | null {
    if (!valor) return null

    const fecha = valor.slice(0, 10)
    if (fecha.length !== 10) return null

    return primerInstanteDesde(valor, medioDia(fecha), (ms) => enCampoLocal(ms, zona)).toISOString()
}
